import { Classes } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { AgentsCategoriesDictionaryType } from "modules/dictionaries/agents-categories/models/agents-categories-dictionary";
import { OrderDictionaryType } from "modules/orders-manage/models/order-dictionary";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgentMailStoreType, MODE_ALL, SendAgentMail } from "../models/agent-mail-store";
import { AttachDialog } from "./AttachDialog";
import { MAIL_ACCEPT, MailComposer } from "./MailComposer";
import { ThreadList } from "./ThreadList";
import { ThreadView } from "./ThreadView";
import { MailAgentOption, MailOrderOption } from "./mail-format";
import styles from "./AgentMailbox.module.scss";

interface AgentMailboxProps {
    store: AgentMailStoreType;
    /** Контрагенты; письма уходят тем, кто агент и с почтой */
    agents: MailAgentOption[];
    categories: AgentsCategoriesDictionaryType;
    orderDictionary: OrderDictionaryType;
    baseUrl: string;
    /** Переписка одного договора (карточка договора) */
    orderId?: string;
}

/**
 * Переписка с агентами: цепочки писем слева, открытая цепочка или новое письмо справа
 */
export const AgentMailbox = observer(
    ({ store, agents, categories, orderDictionary, baseUrl, orderId }: AgentMailboxProps) => {
        const [composing, setComposing] = useState(false);
        const [attaching, setAttaching] = useState(false);

        useEffect(() => {
            categories.load();
            orderDictionary.load();

            store.setFilter({
                orderId: orderId ?? "",
                orderIds: [],
                mode: MODE_ALL,
                agent: "",
                unreadOnly: false,
                search: "",
            });
            store.open(null);
            store.loadThreads();
            store.loadUnread();
        }, [store, categories, orderDictionary, orderId]);

        const orders: MailOrderOption[] = useMemo(
            () => orderDictionary.orders.map((o) => ({ id: o.id, label: `${o.fullInventoryNumber} ${o.label}` })),
            [orderDictionary.orders]
        );

        const fixedOrder = useMemo(
            () => (orderId ? (orders.find((o) => o.id === orderId) ?? null) : null),
            [orderId, orders]
        );

        const categoryItems: SelectItem[] = useMemo(
            () => categories.types.map((c) => ({ id: c.id, label: c.label })),
            [categories.types]
        );

        const upload = useCallback((file: File) => store.uploadFile(file, MAIL_ACCEPT), [store]);

        const send = useCallback(
            async (model: SendAgentMail) => {
                const key = await store.send(model);
                if (key) {
                    setComposing(false);
                }
                return !!key;
            },
            [store]
        );

        const open = useCallback(
            (key: string) => {
                setComposing(false);
                store.open(key);
            },
            [store]
        );

        const thread = store.currentThread;
        const threadAgents = useMemo(() => (thread ? thread.agents.map((a) => a.id) : []), [thread]);

        return (
            <div className={Classes.DIALOG_BODY}>
                <div className={styles.mailbox}>
                    <ThreadList
                        store={store}
                        orderFixed={!!orderId}
                        orderDictionary={orderDictionary}
                        onNew={() => setComposing(true)}
                        onOpen={open}
                    />
                    <div className={styles.main}>
                        {composing && (
                            <MailComposer
                                key="new"
                                full
                                fixedOrder={fixedOrder}
                                agents={agents}
                                categories={categoryItems}
                                orders={orders}
                                baseUrl={baseUrl}
                                onSend={send}
                                onUpload={upload}
                                onCancel={() => setComposing(false)}
                            />
                        )}
                        {!composing && thread && (
                            <ThreadView
                                key={thread.key}
                                store={store}
                                baseUrl={baseUrl}
                                onAttach={() => setAttaching(true)}
                                renderComposer={(done) => (
                                    <MailComposer
                                        threadKey={thread.key}
                                        initialAgents={threadAgents}
                                        agents={agents}
                                        categories={categoryItems}
                                        orders={orders}
                                        baseUrl={baseUrl}
                                        onSend={async (model) => {
                                            const ok = await send(model);
                                            if (ok) {
                                                done();
                                            }
                                            return ok;
                                        }}
                                        onUpload={upload}
                                        onCancel={done}
                                    />
                                )}
                            />
                        )}
                        {!composing && !thread && (
                            <div className={styles.empty}>
                                <span className={styles.emptyIcon}>
                                    <GeneralIcon type="general-msg" size={26} />
                                </span>
                                <div className={styles.emptyTitle}>Выберите переписку слева</div>
                                <div>или напишите агентам новое письмо</div>
                                <PlanrButton
                                    type="greenish"
                                    size="small"
                                    icon="general-plus"
                                    onClick={() => setComposing(true)}
                                >
                                    Написать
                                </PlanrButton>
                            </div>
                        )}
                    </div>
                </div>
                {attaching && thread && (
                    <AttachDialog
                        needAgent={thread.unassigned}
                        senderEmail={thread.senderEmail}
                        currentOrder={thread.order ? { id: thread.order.id, label: thread.order.name } : null}
                        orders={orders}
                        agents={agents}
                        onSave={(order, agent) =>
                            store.attach(thread.key, order ? { id: order.id, name: order.label } : null, agent)
                        }
                        onClose={() => setAttaching(false)}
                    />
                )}
            </div>
        );
    }
);
